import {
  AtSign,
  CalendarDays,
  CircleCheck,
  IdCard,
  ShieldCheck,
  UserRound,
  UsersRound
} from 'lucide-react'
import {
  defineQueryFilterRegistry,
  getQueryFilterDefaultOperators,
  QUERY_FILTER_OPERATOR_OPTIONS,
  type QueryFilterDefinition,
  type QueryFilterRegistry,
  type QueryFilterValues
} from '@/shared/components/data-table'
import type { UserAdminFilterField } from '../models/user-admin'

export type UsersAdminFilterValues = QueryFilterValues<UserAdminFilterField> & {
  fullname: string
  username: string
  email: string
  typeAccount: string[]
  active: string
  isTwoFactorAuthenticationEnabled: string
  roleNames: string
  createdAt: string
}

export const EMPTY_USER_FILTERS: UsersAdminFilterValues = {
  fullname: '',
  username: '',
  email: '',
  typeAccount: [],
  active: '',
  isTwoFactorAuthenticationEnabled: '',
  roleNames: '',
  createdAt: ''
}

const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'Sí' },
  { value: 'false', label: 'No' }
]

export const TYPE_ACCOUNT_LABELS: Record<string, string> = {
  admin: 'Administrador',
  user: 'Normal',
  advisor: 'Asesor'
}

const TYPE_ACCOUNT_OPTIONS = Object.entries(TYPE_ACCOUNT_LABELS).map(([value, label]) => ({
  value,
  label
}))

const STATIC_FIELDS: QueryFilterDefinition<UserAdminFilterField>[] = [
  {
    key: 'typeAccount',
    label: 'Tipo de cuenta',
    mode: 'multi',
    inputType: 'text',
    group: 'essential',
    icon: UsersRound,
    options: TYPE_ACCOUNT_OPTIONS,
    operators: QUERY_FILTER_OPERATOR_OPTIONS.multi,
    defaultOperator: '$in'
  },
  {
    key: 'active',
    label: 'Activo',
    mode: 'single',
    inputType: 'text',
    group: 'essential',
    icon: CircleCheck,
    options: BOOLEAN_OPTIONS,
    operators: QUERY_FILTER_OPERATOR_OPTIONS.equality,
    defaultOperator: '$eq'
  },
  {
    key: 'isTwoFactorAuthenticationEnabled',
    label: '2FA habilitado',
    mode: 'single',
    inputType: 'text',
    group: 'essential',
    icon: ShieldCheck,
    options: BOOLEAN_OPTIONS,
    operators: QUERY_FILTER_OPERATOR_OPTIONS.equality,
    defaultOperator: '$eq'
  },
  {
    key: 'fullname',
    label: 'Nombre',
    mode: 'text',
    inputType: 'text',
    group: 'identity',
    icon: UserRound,
    operators: QUERY_FILTER_OPERATOR_OPTIONS.text,
    defaultOperator: '$contains'
  },
  {
    key: 'username',
    label: 'Usuario',
    mode: 'text',
    inputType: 'text',
    group: 'identity',
    icon: IdCard,
    operators: QUERY_FILTER_OPERATOR_OPTIONS.text,
    defaultOperator: '$contains'
  },
  {
    key: 'email',
    label: 'Correo',
    mode: 'text',
    inputType: 'text',
    group: 'identity',
    icon: AtSign,
    operators: QUERY_FILTER_OPERATOR_OPTIONS.text,
    defaultOperator: '$contains'
  },
  {
    key: 'roleNames',
    label: 'Rol',
    mode: 'text',
    inputType: 'text',
    group: 'identity',
    icon: ShieldCheck,
    operators: QUERY_FILTER_OPERATOR_OPTIONS.text,
    defaultOperator: '$contains'
  },
  {
    key: 'createdAt',
    label: 'Fecha de creación',
    mode: 'text',
    inputType: 'date',
    group: 'dates',
    icon: CalendarDays,
    operators: QUERY_FILTER_OPERATOR_OPTIONS.date,
    defaultOperator: '$gte'
  }
]

const REGISTRY_GROUPS = [
  { key: 'essential', label: 'Esenciales' },
  { key: 'identity', label: 'Identificación' },
  { key: 'dates', label: 'Fechas' }
]

export function createUsersAdminFilterRegistry(): QueryFilterRegistry<UserAdminFilterField> {
  return defineQueryFilterRegistry({
    groups: REGISTRY_GROUPS,
    fields: STATIC_FIELDS,
    search: {
      ariaLabel: 'Buscar usuarios',
      placeholder: 'Buscar por nombre, usuario, correo o rol...'
    }
  })
}

const DEFAULT_REGISTRY = createUsersAdminFilterRegistry()

export const DEFAULT_USER_FILTER_OPERATORS = getQueryFilterDefaultOperators(DEFAULT_REGISTRY)

export function hasActiveUserFilters(values: UsersAdminFilterValues): boolean {
  return Object.values(values).some((value) =>
    Array.isArray(value) ? value.length > 0 : value !== ''
  )
}
